import { useState } from 'react';
import { Icon, Spinner } from '@openedx/paragon';
import {
  RiTimeLine,
  RiHistoryLine,
  RiArrowDownSLine,
  RiArrowDropRightLine,
} from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import { fetchLearnerHomeInit } from '@blend-ed/blendxplatform/api';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';

import { fetchAllNotes } from './api';
import messages from './messages';
import emptyNoteIcon from '../../assets/empty-note-icon.png';

const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// note.updated arrives pre-formatted UTC ("Dec 09, 2015 at 09:31 UTC").
// Parse + render in local time to match formatDate output.
const formatUpdated = (utcString) => {
  if (!utcString) return '';
  const parsed = new Date(utcString.replace(' at ', ' '));
  if (Number.isNaN(parsed.getTime())) return utcString;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const NoteCard = ({ note, isSelected, onClick }) => (
  <button
    type="button"
    className={`courses-note-card ${isSelected ? 'courses-note-card--selected' : ''}`}
    onClick={() => onClick(note)}
  >
    <div className="courses-note-card__highlight">
      <div className="courses-note-card__highlight-text">
        {note.quote}
      </div>
    </div>
    {note.text && (
      <div className="courses-note-card__body">
        <div className="courses-note-card__note-text">
          {note.text}
        </div>
      </div>
    )}
    <div className="courses-note-card__footer">
      <span className="courses-note-card__date">{formatUpdated(note.updated)}</span>
      <a
        href={note.unit?.url || '#'}
        className="courses-note-card__go-link"
        onClick={(e) => e.stopPropagation()}
      >
        <FormattedMessage {...messages.goToLesson} />
        <Icon src={RiArrowDropRightLine} size="inline" />
      </a>
    </div>
  </button>
);

const NotePreview = ({ note, courseName }) => {
  if (!note) return null;

  const unitName = note.unit?.display_name || '';
  const sectionName = note.section?.display_name || '';
  const title = courseName || 'Note';
  const unitUrl = note.unit?.url || '#';

  return (
    <div className="courses-note-preview">
      <div className="courses-note-preview__header">
        <div className="courses-note-preview__header-content">
          <h2 className="courses-note-preview__title">{title}</h2>
          <div className="courses-note-preview__breadcrumb">
            {sectionName && <span>{sectionName}</span>}
            {unitName && (
              <>
                <Icon src={RiArrowDropRightLine} size="inline" />
                <span>{unitName}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="courses-note-preview__content">
        <div className="courses-note-preview__from-lesson">
          <span className="courses-note-preview__label">
            <FormattedMessage {...messages.fromLessonLabel} />
          </span>
          <div className="courses-note-preview__lesson-box">
            <p>{note.quote}</p>
          </div>
        </div>

        {note.text && (
          <div className="courses-note-preview__user-note">
            <span className="courses-note-preview__label">
              <FormattedMessage {...messages.yourNoteLabel} />
            </span>
            <div className="courses-note-preview__note-body">
              <p>{note.text}</p>
            </div>
          </div>
        )}

        <div className="courses-note-preview__metadata">
          <div className="courses-note-preview__meta-row">
            <Icon src={RiTimeLine} size="inline" />
            <span>
              <FormattedMessage
                {...messages.noteCreatedAt}
                values={{ date: formatDate(note.created) }}
              />
            </span>
          </div>
          <div className="courses-note-preview__meta-row">
            <Icon src={RiHistoryLine} size="inline" />
            <span>
              <FormattedMessage
                {...messages.noteLastEdited}
                values={{ date: formatUpdated(note.updated) }}
              />
            </span>
          </div>
        </div>
      </div>

      <div className="courses-note-preview__footer">
        <a href={unitUrl} className="courses-note-preview__open-btn">
          <FormattedMessage {...messages.goToLesson} />
          <Icon src={RiArrowDropRightLine} size="inline" />
        </a>
      </div>
    </div>
  );
};

const NOTES_PAGE_SIZE = 5;

const NotesSection = () => {
  const { formatMessage } = useIntl();
  const [selectedNote, setSelectedNote] = useState(null);
  const [sortOrder, setSortOrder] = useState('latest');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(NOTES_PAGE_SIZE);

  const { data: learnerHomeInit } = useQuery({
    queryKey: ['learnerHomeInit'],
    queryFn: fetchLearnerHomeInit,
  });

  const courses = learnerHomeInit?.courses || [];

  const courseIds = courses
    .map((course) => course?.courseRun?.courseId)
    .filter(Boolean);

  const courseNameById = courses.reduce((acc, course) => {
    const id = course?.courseRun?.courseId;
    if (id) {
      acc[id] = course?.course?.courseName || '';
    }
    return acc;
  }, {});

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ['learnerNotes', courseIds],
    queryFn: () => fetchAllNotes(courseIds),
    enabled: courseIds.length > 0,
  });

  const sortedNotes = [...notes].sort((a, b) => {
    const dateA = new Date(a.created);
    const dateB = new Date(b.created);
    return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
  });

  if (!selectedNote && sortedNotes.length > 0) {
    setSelectedNote(sortedNotes[0]);
  }

  if (isLoading) {
    return (
      <div className="courses-notes-section courses-notes-section--loading">
        <Spinner animation="border" screenReaderText={formatMessage(messages.notesLoading)} />
      </div>
    );
  }

  if (notes.length === 0) {
    return (
      <div className="courses-notes-empty">
        <img
          src={emptyNoteIcon}
          alt=""
          className="courses-notes-empty__icon"
        />
        <div className="courses-notes-empty__text">
          <span className="courses-notes-empty__title">
            <FormattedMessage {...messages.noNotesYet} />
          </span>
          <p className="courses-notes-empty__description">
            <FormattedMessage {...messages.noNotesDescription} />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="courses-notes-section">
      <div className="courses-notes-section__left">
        <div className="courses-notes-section__header">
          <h2 className="courses-notes-section__title">
            <FormattedMessage {...messages.notesHeading} />
          </h2>
          <div className="courses-notes-section__sort">
            <span className="courses-notes-section__sort-label">
              <FormattedMessage {...messages.sortByLabel} />
            </span>
            <div className="courses-notes-section__sort-dropdown">
              <button
                type="button"
                className="courses-notes-section__sort-btn"
                onClick={() => setSortDropdownOpen((prev) => !prev)}
              >
                <span>
                  {formatMessage(
                    sortOrder === 'latest' ? messages.sortLatest : messages.sortOldest,
                  )}
                </span>
                <Icon src={RiArrowDownSLine} size="inline" />
              </button>
              {sortDropdownOpen && (
                <div className="courses-notes-section__sort-menu">
                  <button
                    type="button"
                    className={`courses-notes-section__sort-option ${sortOrder === 'latest' ? 'courses-notes-section__sort-option--active' : ''}`}
                    onClick={() => { setSortOrder('latest'); setSortDropdownOpen(false); }}
                  >
                    <FormattedMessage {...messages.sortLatest} />
                  </button>
                  <button
                    type="button"
                    className={`courses-notes-section__sort-option ${sortOrder === 'oldest' ? 'courses-notes-section__sort-option--active' : ''}`}
                    onClick={() => { setSortOrder('oldest'); setSortDropdownOpen(false); }}
                  >
                    <FormattedMessage {...messages.sortOldest} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="courses-notes-section__list">
          {sortedNotes.slice(0, visibleCount).map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              isSelected={selectedNote?.id === note.id}
              onClick={setSelectedNote}
            />
          ))}
        </div>

        {sortedNotes.length > visibleCount && (
          <button
            type="button"
            className="courses-notes-section__show-more"
            onClick={() => setVisibleCount((c) => c + NOTES_PAGE_SIZE)}
          >
            <FormattedMessage {...messages.showMoreNotes} />
          </button>
        )}
      </div>

      <NotePreview note={selectedNote} courseName={courseNameById[selectedNote?.course_id]} />
    </div>
  );
};

export default NotesSection;
